import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import Button from "../../../../../../components/shared/Button";
import Tooltip from "../../../../../../components/shared/Tooltip";
import Checkbox from "../../../../../../components/shared/inputs/Checkbox";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useScrollContext } from "../../../hooks/context/useScrollContext";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useColumns } from "../../../hooks/grid/useColumns";
import { useGroupCollapse } from "../../../hooks/grid/useGroupCollapse";
import { cn } from "../../../../../../lib/utils";

function Corner() {
    const { t } = useTranslation();
    const baseColumns = useColumns();
    const [{ mode }] = useFilters();
    const {
        dailyInfo,
        selectedEvents,
        dispatch,
        events,
        columns: columnsConfig,
        type
    } = useScheduleContext();
    const { cornerHeaderRef, handlePaneWheel } = useScrollContext();
    const { hasGroups, allCollapsed, toggleAll } = useGroupCollapse();

    const columns = useMemo(() => {
        return baseColumns.filter((col: any) => {
            return typeof columnsConfig?.[col.field] === "boolean"
                ? columnsConfig[col.field]
                : col.field === "name";
        });
    }, [baseColumns, columnsConfig]);

    const shiftEvents = useMemo(() => {
        return events.filter((e: any) => e.type !== "unavailable" && e.type !== "open-shifts");
    }, [events]);

    const allSelected = useMemo(() => {
        return shiftEvents.length > 0 && shiftEvents.every((e: any) =>
            selectedEvents.some((s: any) => s.id === e.id)
        );
    }, [shiftEvents, selectedEvents]);

    const someSelected = useMemo(() => {
        return !allSelected && shiftEvents.some((e: any) =>
            selectedEvents.some((s: any) => s.id === e.id)
        );
    }, [allSelected, shiftEvents, selectedEvents]);

    const onSelectAll = useCallback(() => {
        if (allSelected) {
            dispatch({ type: "select-events", payload: [] });
        } else {
            dispatch({ type: "select-events", payload: shiftEvents });
        }
    }, [allSelected, shiftEvents, dispatch]);

    return (
        <div ref={cornerHeaderRef} className="corner-header-container" onWheel={handlePaneWheel}>
            <div
                className="corner-header-grid"
                style={{
                    // Fill the header row: the daily info row stretches to match the header's daily info row
                    height: "100%",
                    gridTemplateRows: "auto 1fr",
                    gridTemplateColumns: columns
                        .map((col: any) =>
                            col.field === "name"
                                ? "minmax(150px, 1fr)"
                                : "minmax(90px, 150px)"
                        )
                        .join(" ")
                }}
            >
                {columns.map((column: any, index: number) => (
                    <div
                        key={column.field}
                        className={cn(
                            mode === "day" ? "tw-h-14" : "tw-h-[5.75rem]",
                            dailyInfo && "tw-border-b tw-min-h-[3.25rem]",
                            dailyInfo && mode !== "day" && "tw-min-h-[5.75rem]",
                            !["team", "home"].includes(type) && "tw-min-h-10 tw-h-10",
                            index === 0 && (mode === "day" || hasGroups) && "tw-flex tw-items-center tw-gap-2 tw-px-3"
                        )}
                    >
                        {/* {column.text} */}
                        {index === 0 && hasGroups && (
                            <Tooltip
                                side="bottom"
                                trigger={
                                    <Button
                                        variant="ghost"
                                        onClick={toggleAll}
                                        aria-label={allCollapsed ? t("expand-all") : t("collapse-all")}
                                        className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-p-0 tw-rounded hover:tw-bg-primary/20 tw-transition-colors tw-shrink-0"
                                    >
                                        {allCollapsed
                                            ? <ChevronsUpDown size={16} className="tw-text-primary" />
                                            : <ChevronsDownUp size={16} className="tw-text-primary" />}
                                    </Button>
                                }
                            >
                                {allCollapsed ? t("expand-all") : t("collapse-all")}
                            </Tooltip>
                        )}
                        {mode === "day" && index === 0 && type !== "my-profile" && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Checkbox
                                    className="tw-bg-white"
                                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                                    onCheckedChange={onSelectAll}
                                />
                                <span className="tw-text-sm tw-text-neutral-600">
                                    {allSelected ? t("deselect-all") : t("select-all")}
                                </span>
                            </div>
                        )}
                    </div>
                ))}

                <div className="tw-w-full">
                    <div
                        className="tw-h-full"
                        style={{
                            gridTemplateColumns: columns
                                .map((col: any) =>
                                    col.field === "name"
                                        ? "minmax(150px, 1fr)"
                                        : "minmax(90px, 150px)"
                                )
                                .join(" ")
                        }}
                    >
                        {dailyInfo && (
                            <div
                                className="corner-header-cell tw-w-full tw-text-sm"
                                style={{ borderRight: 0, height: "100%" }}
                            >
                                {t("daily-info")}
                            </div>
                        )}
                        {/* {columns.slice(0, columns.length - 1).map((column: any) => (
                            <div
                                key={column.field}
                                className={cn(dailyInfo && "tw-border-b")}
                                style={{ height: dailyInfoHeight }}
                            >
                            </div>
                        ))} */}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Corner;
